/**
 * Spending Tools paywall — the paid-launch upgrade surface (card A1).
 *
 * RC-Offerings-driven on native: it reads the `spending tools` offering BY
 * IDENTIFIER (never `.current`, so it stays dark until the flag flips) and
 * renders whatever packages that offering returns — monthly + annual, annual
 * shown as a saving. On web (card T2) there is no RC Offering, so it builds
 * the same shape from the `WEB_PRICES` env-configured Stripe price IDs
 * (`lib/paidLaunch.ts`) — PaywallModal only mounts this component on web when
 * `webPricesConfigured()` is true, so this stays a degrade-gracefully-to-error
 * path here, not the primary gate. Prices come from the Offering / WEB_PRICES,
 * never hard-coded (LEAD_PRICE is retired here). It renders two states off
 * trial eligibility: trial-available ("Free for <period>, then $X ... on
 * <date>") and trial-used (price only). Trial-length copy is store-derived —
 * "1 month" on native, "30 days" on web (policy on TrialDescriptor).
 *
 * Purchase mechanics differ by platform (see
 * `docs/operations/2026/Q3/IOS_TRIAL_OFFERS.md`):
 * - iOS: one SKU per plan, purchased with plain `purchasePackage`. The trial is
 *   an App Store Connect INTRODUCTORY offer that Apple applies automatically to
 *   eligible Apple IDs — nothing is signed and nothing is passed. Because Apple
 *   decides, the trial COPY is gated on real per-Apple-ID eligibility
 *   (`useTrialEligibility`), never on the product's static intro metadata.
 * - Android: Play developer-determined offers ride on the package itself via
 *   plain `purchasePackage` — Play/RevenueCat pick the eligible offer.
 * - Web: Stripe Elements (`StripeCheckoutStep`) + `createSubscription(...,
 *   { requestTrial })`, card T2. The server's `trialApplied` in the response
 *   is authoritative — if we showed trial copy but the server declined, the
 *   success screen says so honestly instead of pretending it happened.
 *
 * Degrades gracefully to a single monthly package when that's all the
 * Offering/WEB_PRICES holds. Brand tokens only; no branded tiers; honest
 * about why we charge.
 */
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { Capacitor } from '@capacitor/core';
import { Icons } from '@shared/constants';
import { logWarn } from '@/lib/log';
import { trackEvent } from '@shared/services/analyticsTracker';
import { trackSubscriptionPurchase } from '@shared/services/conversionTracking';
import { markPurchaseAttempt } from '@/lib/strandedPurchaseConversion';
import { recordSubscriptionAttribution } from '@shared/services/attributionService';
import { auth } from '@shared/services/firebase';
import {
  getOfferingByIdentifier,
  purchasePackage,
  restorePurchases,
  type NativeOffering,
  type NativePackage,
  type PurchaseResult,
} from '../../services/purchasesService';
import { useSubscription } from '../../context/SubscriptionContext';
import { useTrialEligibility } from '../../hooks/useTrialEligibility';
import { SpendingToolsBenefits } from './SpendingToolsBenefits';
import { StripeCheckoutStep } from './StripeCheckoutStep';
import { TrialNotifyPrompt } from './TrialNotifyPrompt';
import { emphasizeBrand } from '../../lib/emphasizeBrand';
import { SpendingToolsPaywallFunnel } from './SpendingToolsPaywallFunnel';
import { WebRestoreGuidance } from './WebRestoreGuidance';
import { FUNNEL_COLUMN } from '@/lib/funnelLayout';
import {
  PAID_OFFERING_ID,
  WEB_PRICES,
  WEB_TRIAL,
  annualMonthlyEquivalent,
  annualSavingsPercent,
  formatTrialTerms,
  formatUsd,
  stripWholeDollarCents,
  type TrialDescriptor,
} from '../../lib/paidLaunch';

interface SpendingToolsPaywallProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  /** Seam notifier: called when the user initiates checkout. */
  onPurchaseStart?: () => void;
  /**
   * Dev/web preview: render with this offering instead of fetching from
   * RevenueCat (which only resolves on native) or building from WEB_PRICES
   * (which needs real env vars). Used by the sandbox and tests.
   */
  previewOffering?: NativeOffering;
  /**
   * Sandbox/test override for trial eligibility — forces the trial-available
   * vs trial-used copy without a real userContent flag. Undefined = read the
   * live seam.
   */
  trialAvailableOverride?: boolean;
  /** Test/sandbox override for the purchase call (defaults to native purchase / web Stripe step). */
  onPurchaseOverride?: (pkg: NativePackage) => Promise<PurchaseResult>;
  /**
   * Pre-login mounts only (value_onboarding carousel): renders an
   * "Already a member? Sign in" link so an existing member routes to login
   * instead of buying a second subscription on a fresh anonymous uid.
   * Post-login mounts leave this unset — restore/manage cover it there.
   */
  onSignIn?: () => void;
  /**
   * Post-login gate mounts (PaidAccessGate): renders a quiet "Sign out"
   * escape in the funnel header — the mirror of the carousel's "Sign in".
   * The gate auto-opens this paywall full-screen with no dismiss, so without
   * it an authed user (a non-grandfathered legacy user, a fresh signup who
   * skipped the pre-login sell) has no way out but purchase.
   */
  onSignOut?: () => void;
  /**
   * Which treatment to wear (Justin, 2026-07-21). The purchase flow is
   * identical either way — this picks the SKIN and the framing.
   *
   * - `'funnel'` (green): the ACQUISITION surface, pre- or post-login. Same
   *   screen the Android carousel closes on, so the handoff is seamless. Shows
   *   the trial timeline for eligible users and drops it for returning ones
   *   (existing subscription on a new device, or a second account) — see
   *   SpendingToolsPaywallFunnel.
   * - `'app'` (white, the default): the surface for someone already inside the
   *   product — winback, an expired membership, an in-app upgrade prompt. A
   *   returning member shouldn't be dropped back into onboarding chrome.
   */
  surface?: 'app' | 'funnel';
}

type FlowState = 'loading' | 'select' | 'payment' | 'purchasing' | 'restoring' | 'success' | 'error';
type PlanChoice = 'monthly' | 'annual';

/**
 * Web (card T2) has no RC Offering, so we build the same `NativeOffering`
 * shape from the env-configured Stripe price IDs. `productId` on these
 * synthetic packages holds the STRIPE PRICE ID (not a store product id) —
 * that's what `handlePurchase`'s web branch passes to `createSubscription`.
 * `_rcPackage` is unused on this path (never passed to a RevenueCat call).
 */
function buildWebOffering(): NativeOffering {
  // Web trial copy is "30 days" — see WEB_TRIAL's note in lib/paidLaunch.ts.
  const trial: TrialDescriptor = WEB_TRIAL;

  const monthly: NativePackage | null = WEB_PRICES.monthly.priceId
    ? {
        identifier: 'web_monthly',
        packageType: 'MONTHLY',
        priceString: formatUsd(WEB_PRICES.monthly.amount),
        price: WEB_PRICES.monthly.amount,
        title: 'Spending Tools Monthly',
        description: 'Monthly',
        productId: WEB_PRICES.monthly.priceId,
        trial,
        _rcPackage: null,
      }
    : null;

  const annual: NativePackage | null = WEB_PRICES.annual.priceId
    ? {
        identifier: 'web_annual',
        packageType: 'ANNUAL',
        priceString: formatUsd(WEB_PRICES.annual.amount),
        price: WEB_PRICES.annual.amount,
        title: 'Spending Tools Annual',
        description: 'Annual',
        productId: WEB_PRICES.annual.priceId,
        trial,
        _rcPackage: null,
      }
    : null;

  return {
    identifier: PAID_OFFERING_ID,
    monthly,
    annual,
    allPackages: [monthly, annual].filter((p): p is NativePackage => !!p),
  };
}

export const SpendingToolsPaywall: React.FC<SpendingToolsPaywallProps> = ({
  isOpen,
  onClose,
  onSuccess,
  onPurchaseStart,
  previewOffering,
  trialAvailableOverride,
  onPurchaseOverride,
  onSignIn,
  onSignOut,
  surface = 'app',
}) => {
  const { refreshSubscription, subscriptionInfo, openManageSubscription } = useSubscription();

  const isNativePlatform = Capacitor.isNativePlatform();

  // Already-covered guard (Justin, 2026-07-20): never SELL to someone whose
  // active subscription is still renewing — there's nothing to buy. Mirrors
  // the server's checkout-time cross-store refusal, but BEFORE we show prices
  // or ask for a card. willRenew === false always sells: the sub is cancelled
  // and lapses on its own, so this is either the in-period winback (same
  // store) or a store SWITCH (e.g. cancelled App Store sub buying on web —
  // the server gate passes this through too). Missing/unknown renewal state
  // counts as renewing, matching the server's conservative read.
  const activeSub = subscriptionInfo?.hasActiveSubscription ? subscriptionInfo : null;
  const alreadyCovered = !!activeSub && activeSub.willRenew !== false;
  const nativeBilledElsewhere = alreadyCovered && !isNativePlatform
    && (activeSub?.store === 'app_store' || activeSub?.store === 'play_store');
  // Android + cancelled Play-billed sub: Google Play refuses in-app repurchase
  // of a still-owned subscription (ITEM_ALREADY_OWNED), so the sell would dead-
  // end at an error sheet. Play's supported winback is its own subscription
  // center — route there. (iOS is the opposite: Apple's sheet RESUMES a
  // cancelled sub on repurchase, so the iOS winback keeps the normal sell.)
  const playResubscribe = isNativePlatform && Capacitor.getPlatform() === 'android'
    && activeSub?.store === 'play_store' && activeSub?.willRenew === false;
  // Promotional grants (grandfather comp, friends & family) have no billing
  // relationship — no store subscription, no Stripe customer — so there is
  // nothing for "Manage subscription" to open.
  const complimentary = alreadyCovered && activeSub?.store === 'promotional';

  const [flowState, setFlowState] = useState<FlowState>('loading');
  const [offering, setOffering] = useState<NativeOffering | null>(null);
  const [choice, setChoice] = useState<PlanChoice>('monthly'); // monthly-led (§4)
  // Price interaction was previously unmeasured — the monthly/annual choice
  // only surfaced at purchase_started. One event per toggle.
  const selectPlan = (plan: PlanChoice) => {
    setChoice(plan);
    trackEvent('paywall_plan_selected', { plan });
  };
  const [errorMessage, setErrorMessage] = useState('');
  // We showed trial terms and the charge that followed had no trial in it.
  // Both platforms can land here, for different reasons:
  // - web: the server declined the requested trial (e.g. `freeTrialUsed`
  //   flipped true between page load and checkout);
  // - native: the store disagreed with its own pre-purchase eligibility answer
  //   (storefront the offer doesn't cover, sandbox drift).
  // Prevention comes first — pinned StoreKit 2, fail-closed eligibility — but
  // when the gap opens anyway, say so on the success screen rather than let the
  // user discover it on their statement.
  const [trialDeclined, setTrialDeclined] = useState(false);
  // The purchase hit already-owned and the auto-restore brought the existing
  // membership to this account — the success screen says what happened
  // instead of pretending a new purchase went through.
  const [restoredExisting, setRestoredExisting] = useState(false);
  // ...and that restored membership is cancelled (access until expiry, no
  // renewal). Restoring is NOT a resubscribe — say so rather than let the
  // user believe they just signed back up.
  const [restoredNotRenewing, setRestoredNotRenewing] = useState(false);

  // Fetch/build pricing when the paywall opens.
  useEffect(() => {
    if (!isOpen) return;
    setFlowState('loading');
    setErrorMessage('');
    setChoice('monthly');

    if (previewOffering) {
      setOffering(previewOffering);
      setFlowState('select');
      return;
    }

    if (!isNativePlatform) {
      // Web (card T2) — no RC Offerings; build from WEB_PRICES.
      const webOffering = buildWebOffering();
      setOffering(webOffering);
      setFlowState(webOffering.monthly || webOffering.annual ? 'select' : 'error');
      if (!webOffering.monthly && !webOffering.annual) {
        setErrorMessage('Unable to load pricing. Please try again later.');
      }
      return;
    }

    getOfferingByIdentifier(PAID_OFFERING_ID).then((off) => {
      setOffering(off);
      setFlowState(off?.monthly || off?.annual ? 'select' : 'error');
      if (!off?.monthly && !off?.annual) {
        setErrorMessage('Unable to load pricing. Please try again later.');
      }
    });
  }, [isOpen, previewOffering, isNativePlatform]);

  const monthly = offering?.monthly ?? null;
  const annual = offering?.annual ?? null;
  // Degrade gracefully: if the Offering only carries one term, force that choice.
  const hasBoth = !!monthly && !!annual;
  const effectiveChoice: PlanChoice = hasBoth ? choice : (annual && !monthly ? 'annual' : 'monthly');
  const selected = effectiveChoice === 'annual' ? annual : monthly;

  const savingsPct = useMemo(
    () => annualSavingsPercent(monthly?.price, annual?.price),
    [monthly?.price, annual?.price],
  );
  const annualPerMonth = useMemo(
    () => annualMonthlyEquivalent(annual?.price),
    [annual?.price],
  );

  // Trial copy is gated on REAL eligibility for the plan in front of the user,
  // resolved per product once the offering loads — on iOS that's Apple's answer
  // for this Apple ID, not the product's static intro metadata. See
  // useTrialEligibility for the per-platform split.
  const productIds = useMemo(
    () => [monthly?.productId, annual?.productId].filter((id): id is string => !!id),
    [monthly?.productId, annual?.productId],
  );
  const eligibility = useTrialEligibility(productIds);
  const trialAvailable = trialAvailableOverride ?? eligibility.isTrialAvailableFor(selected?.productId);
  // Until eligibility resolves we don't know WHICH lead this person gets —
  // "Try all of enough. free." or the returning-customer "Come back to
  // enough." (deleted-email registry ≡ freeTrialUsed, 2026-07-20 ruling).
  // Hold the loading state rather than flash one and swap it: showing trial
  // copy we'd retract is a broken promise, and greeting a brand-new user as
  // returning is just wrong.
  const eligibilityResolving = trialAvailableOverride === undefined && eligibility.loading;

  const handleClose = useCallback(() => {
    setFlowState('loading');
    setOffering(null);
    setErrorMessage('');
    setTrialDeclined(false);
    setRestoredExisting(false);
    setRestoredNotRenewing(false);
    onClose();
  }, [onClose]);

  /**
   * A completed purchase closes the paywall straight away: the gate lets the
   * member into onboarding, whose first step is connecting a bank, so the
   * "You're all set — connect your accounts — Done" screen was a stop that
   * said the same thing (2026-10-06 walk). A declined trial is news the
   * person has to see before they are charged, so that case keeps the
   * success screen; restores keep it too (they say what was found).
   */
  const finishPurchase = useCallback((declined: boolean) => {
    if (declined) setFlowState('success');
    else handleClose();
  }, [handleClose]);

  /** Shared success/cancel/error branching for both the test override and the native purchase path. */
  const handlePurchaseResult = useCallback(async (result: PurchaseResult) => {
    if (result.success) {
      // Only an EXPLICIT false counts: `undefined` means the store never told
      // us (the purchase watchdog path sees no CustomerInfo), and guessing
      // "no trial" there would invent a problem the user doesn't have.
      const declined = trialAvailable && result.trialApplied === false;
      if (declined) {
        logWarn('[SpendingToolsPaywall] store applied no trial despite eligibility saying yes');
      }
      setTrialDeclined(declined);
      // Point of sale owns the new-purchase conversion events (see
      // trackSubscriptionPurchase): the carousel sells on an anonymous uid the
      // entitlement observer never sees. `trialApplied` is the store's own
      // answer when it gave one; eligibility is the best classification when
      // the watchdog path left it unknown.
      await trackSubscriptionPurchase({
        tier: 'spending_tools',
        isTrial: (result.trialApplied ?? trialAvailable) === true,
        value: selected?.price,
        uid: auth.currentUser?.uid,
      });
      if (auth.currentUser) {
        recordSubscriptionAttribution(auth.currentUser.uid).catch(() => {});
      }
      try { await refreshSubscription(); } catch { /* best effort */ }
      onSuccess?.();
      finishPurchase(declined);
    } else if (result.userCancelled) {
      setFlowState('select');
    } else if (result.alreadyOwned && !onSignIn) {
      // The store account owns the sub but this uid holds no entitlement (the
      // already-covered guard would have blocked the sell otherwise) — the
      // second-account / new-uid case, where a fresh purchase can NEVER
      // succeed. Restore is the only door, so walk through it rather than
      // strand the user on an error (Justin, 2026-07-21). Receipt-verified,
      // so the worst a wrong guess costs is one round-trip to the store.
      // Post-login mounts only: pre-login (`onSignIn` set) the membership
      // likely belongs on the account they haven't signed into yet — restore
      // onto the anonymous uid would be a detour, so route to sign-in below.
      setFlowState('restoring');
      const restore = await restorePurchases();
      // The store said "owned", so a real recovery must surface a
      // store-billed entitlement — a web or promotional one riding along in
      // CustomerInfo proves nothing was restored.
      if (restore.success && (restore.storeBilledEntitlements ?? []).length > 0) {
        trackEvent('paywall_already_owned_recovered', { outcome: 'restored' });
        setRestoredExisting(true);
        setRestoredNotRenewing(restore.willRenew === false);
        setFlowState('success');
        try { await refreshSubscription(); } catch { /* best effort */ }
        onSuccess?.();
      } else {
        // The store says "owned" but restore surfaced nothing — a state we
        // can't fix from here. Fall back to the honest guidance copy.
        trackEvent('paywall_already_owned_recovered', { outcome: 'restore_empty' });
        setErrorMessage(result.error ?? 'Purchase failed. Please try again.');
        setFlowState('select');
      }
    } else if (result.alreadyOwned) {
      // Pre-login (carousel): the sub already backs an account — theirs. Point
      // at sign-in (the link is right below) instead of the store-settings
      // copy, which is wrong guidance for a second-account situation.
      setErrorMessage('You already have this membership. Sign in to the account you used before, or restore purchases to bring it to this one.');
      setFlowState('select');
    } else {
      setErrorMessage(result.error ?? 'Purchase failed. Please try again.');
      setFlowState('select');
    }
  }, [onSuccess, onSignIn, refreshSubscription, trialAvailable, selected, finishPurchase]);

  const handlePurchase = useCallback(async () => {
    if (!selected) return;
    onPurchaseStart?.();
    setErrorMessage('');

    if (onPurchaseOverride) {
      setFlowState('purchasing');
      await handlePurchaseResult(await onPurchaseOverride(selected));
      return;
    }

    if (!isNativePlatform) {
      // Web (card T2): open the Stripe Elements step. The actual charge / trial
      // request happens there via createSubscription, not here.
      setFlowState('payment');
      return;
    }

    // Native: one SKU per plan, purchased plain. On iOS Apple applies the
    // introductory offer itself if this Apple ID is eligible; on Android
    // Play/RevenueCat pick the eligible offer. Nothing to pass either way.
    setFlowState('purchasing');
    if (auth.currentUser) markPurchaseAttempt(auth.currentUser.uid);
    await handlePurchaseResult(await purchasePackage(selected));
  }, [selected, onPurchaseOverride, onPurchaseStart, isNativePlatform, handlePurchaseResult]);

  /** Web-only: called by StripeCheckoutStep once the Stripe subscription is confirmed. */
  const handleWebPaymentSuccess = useCallback(async (info: { trialApplied?: boolean }) => {
    const declined = trialAvailable && info.trialApplied === false;
    if (declined) {
      logWarn('[SpendingToolsPaywall] server declined the requested trial; charged immediately');
    }
    setTrialDeclined(declined);
    // Same point-of-sale ownership as the native path — the server's
    // trialApplied answer is authoritative for classification.
    await trackSubscriptionPurchase({
      tier: 'spending_tools',
      isTrial: (info.trialApplied ?? trialAvailable) === true,
      value: selected?.price,
      uid: auth.currentUser?.uid,
    });
    if (auth.currentUser) {
      recordSubscriptionAttribution(auth.currentUser.uid).catch(() => {});
    }
    onSuccess?.();
    finishPurchase(declined);
  }, [trialAvailable, onSuccess, selected, finishPurchase]);

  const handleRestore = useCallback(async () => {
    setFlowState('restoring');
    setErrorMessage('');
    const result = await restorePurchases();
    const storeBilled = result.storeBilledEntitlements ?? [];
    if (result.success && storeBilled.length > 0) {
      // A store-billed membership actually surfaced — the one case Restore
      // can genuinely fix. Same honest framing as the already-owned recovery:
      // an existing membership was found, and if it's cancelled-but-active,
      // say so rather than implying it continues.
      setRestoredExisting(true);
      setRestoredNotRenewing(result.willRenew === false);
      setFlowState('success');
      try { await refreshSubscription(); } catch { /* best effort */ }
      onSuccess?.();
    } else if (result.success && result.entitlements.length > 0) {
      // Active membership, but none of it billed through a device store —
      // web (Stripe) or complimentary. Restore didn't (and can't) change
      // anything, so a success screen would imply it fixed whatever brought
      // the user here (2026-07-29 TestFlight: restoring over a cancelled
      // Stripe sub reported success while the ends-soon banner rightly
      // stayed). Say what's true instead.
      const webBilled = (result.activeStores ?? []).includes('STRIPE');
      setErrorMessage(webBilled
        ? `Your membership is billed through the web, so there's nothing to restore on this device.${
          result.willRenew === false
            ? ' It’s currently set to end — you can turn renewal back on from Profile → Manage subscription.'
            : ''}`
        : 'Your current access is complimentary, so there are no purchases to restore.');
      setFlowState('select');
    } else if (result.success) {
      setErrorMessage('No previous purchases found.');
      setFlowState('select');
    } else {
      setErrorMessage(result.error ?? 'Restore failed. Please try again.');
      setFlowState('select');
    }
  }, [onSuccess, refreshSubscription]);

  if (!isOpen) return null;

  // Honest CTA + terms for the selected plan, gated by trial eligibility.
  // Whole-dollar prices drop the cents everywhere they render ("$15", never
  // "$15.00") — the store's own strings carry them, so strip at display time.
  const suffix = effectiveChoice === 'annual' ? '/year' : '/month';
  const trialForCopy = trialAvailable ? (selected?.trial ?? null) : null;
  const termsLine = selected
    ? formatTrialTerms(stripWholeDollarCents(selected.priceString), suffix, trialForCopy, new Date())
    : '';
  // Trial-eligible users get the explicit trial CTA; everyone else gets the
  // quiet "Join" (Justin's 2026-07-20 redesign) — the plan cards carry the
  // price, so the button never needs to.
  const ctaLabel = trialForCopy ? 'Start free trial' : 'Join';

  // One purchase flow, two skins. Only the SELECT state differs structurally
  // (the funnel screen is a different layout, not a recolour) — every other
  // state is the same markup reading its colours from `tone`.
  //
  // The Stripe Elements step keeps the light card on BOTH surfaces: the card
  // fields are vendor-rendered and legible on light only, so a dark chrome
  // around them would be worse than the seam.
  const isFunnel = surface === 'funnel';
  const funnelChrome = isFunnel && flowState !== 'payment';
  const tone = funnelChrome
    ? {
        spinner: 'border-sand/20 border-t-foliage',
        muted: 'text-sand/70',
        heading: 'text-branch',
        body: 'text-sand/75',
        disc: 'bg-sand/10',
        discIcon: 'text-foliage',
        primaryBtn: 'bg-foliage text-growth hover:bg-foliage/90',
        quietBtn: 'text-sand/60 hover:text-sand',
        errorDisc: 'bg-wildfire/20',
        fine: 'text-sand/50',
        rule: 'border-sand/15',
      }
    : {
        spinner: 'border-mist border-t-foliage',
        muted: 'text-growth/70',
        heading: 'text-growth',
        body: 'text-growth/70',
        disc: 'bg-growth/20',
        discIcon: 'text-growth',
        primaryBtn: 'bg-growth text-cloud hover:bg-growth/90',
        quietBtn: 'text-growth/50 hover:text-growth',
        errorDisc: 'bg-wildfire/10',
        fine: 'text-growth/50',
        rule: 'border-mist',
      };

  const modalContent = (
    <div
      className={funnelChrome
        ? 'fixed inset-0 z-[300] flex flex-col overflow-hidden bg-growth'
        : 'fixed inset-0 z-[300] flex items-center justify-center p-4 bg-growth/30 backdrop-blur-sm'}
      onClick={funnelChrome ? undefined : handleClose}
    >
      {/* The funnel's signature blobs — same two as the carousel
          and the auth screen, so the acquisition path is one surface. */}
      {funnelChrome && (
        <>
          <div
            aria-hidden
            className="absolute z-0 rounded-full blur-[60px]"
            style={{ width: 300, height: 300, background: 'rgba(197,160,89,.08)', top: '18%', left: '-12%' }}
          />
          <div
            aria-hidden
            className="absolute z-0 rounded-full blur-[60px]"
            style={{ width: 260, height: 260, background: 'rgba(81,107,148,.08)', bottom: '8%', right: '-12%' }}
          />
        </>
      )}

      <div
        className={funnelChrome
          ? `relative z-[2] flex flex-1 flex-col overflow-y-auto px-7 pb-[calc(env(safe-area-inset-bottom)+1.25rem)] pt-[calc(env(safe-area-inset-top)+1.4rem)] ${FUNNEL_COLUMN}`
          : 'bg-cloud rounded-2xl shadow-xl w-full max-w-md max-h-[90dvh] flex flex-col overflow-hidden'}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header. The funnel surface wears the wordmark like every other step
            of the funnel; the app modal keeps its dismiss affordance. */}
        {funnelChrome ? (
          <div className="relative flex items-center justify-center">
            <h2 className="sr-only">Become a member</h2>
            <img src="/assets/logos/enough wordmark_beige.png" alt="enough." className="h-9 w-auto" />
            {/* Same placement and weight as the carousel's "Sign in" — an
                escape, not a competing action. Hidden mid-purchase (signing
                out under an in-flight charge helps no one) and after success
                (they're a member; the gate dissolves on Done). */}
            {onSignOut && (flowState === 'loading' || flowState === 'select' || flowState === 'error') && (
              <button
                onClick={onSignOut}
                className="absolute right-0 text-xs font-medium text-sand/55 transition-colors hover:text-sand"
              >
                Sign out
              </button>
            )}
          </div>
        ) : (
          <div className="flex items-center justify-end px-2 pt-2">
            <h2 className="sr-only">Become a member</h2>
            <button
              onClick={handleClose}
              className="p-2 text-growth/50 hover:text-growth transition-colors rounded-full hover:bg-sand"
            >
              <Icons.X className="w-5 h-5" />
            </button>
          </div>
        )}

        <div className={funnelChrome ? 'flex flex-1 flex-col' : 'flex-1 overflow-y-auto p-5'}>
          {(flowState === 'loading' || (flowState === 'select' && !alreadyCovered && !playResubscribe && eligibilityResolving)) && (
            <div className="flex flex-col items-center justify-center py-12">
              <div className={`w-10 h-10 border-4 rounded-full animate-spin ${tone.spinner}`} />
              <p className={`mt-4 ${tone.muted}`}>Loading pricing...</p>
            </div>
          )}

          {flowState === 'select' && (alreadyCovered || playResubscribe) && (
            <div className="flex flex-col items-center justify-center py-10 text-center">
              <div className={`w-16 h-16 rounded-full flex items-center justify-center mb-4 ${tone.disc}`}>
                <Icons.Check className={`w-8 h-8 ${tone.discIcon}`} />
              </div>
              <h3 className={`text-lg font-serif mb-2 ${tone.heading}`}>
                {playResubscribe ? 'Resubscribe in Google Play' : 'You’re already a member'}
              </h3>
              <p className={`text-sm mb-6 px-2 ${tone.body}`}>
                {playResubscribe
                  ? 'Your membership is billed through Google Play. To restart it, renew it from your Play subscriptions — it only takes a moment.'
                  : nativeBilledElsewhere
                    ? `Your membership is billed through ${activeSub?.store === 'app_store' ? 'the App Store' : 'Google Play'}. To make changes — including resubscribing — manage it there.`
                    : complimentary
                      ? 'Your membership is active and included with your account, so there’s nothing to buy or manage here.'
                      : 'Your membership is active, so there’s nothing to buy here. You can review or change it anytime.'}
              </p>
              {!complimentary && (
                <button
                  onClick={() => { void openManageSubscription(); }}
                  className={`px-6 py-2.5 rounded-xl transition-colors font-medium mb-3 ${tone.primaryBtn}`}
                >
                  {playResubscribe ? 'Open Google Play' : 'Manage subscription'}
                </button>
              )}
              <button
                onClick={handleClose}
                className={`text-sm transition-colors ${tone.quietBtn}`}
              >
                Done
              </button>
            </div>
          )}

          {/* The acquisition surface: literally the screen the Android carousel
              closes on. Timeline for trial-eligible users, no timeline for the
              returning ones (existing sub on a new device, second account). */}
          {funnelChrome && flowState === 'select' && !alreadyCovered && !playResubscribe && !eligibilityResolving && selected && (
            <SpendingToolsPaywallFunnel
              trialAvailable={trialAvailable}
              trial={trialForCopy}
              monthly={monthly}
              annual={annual}
              choice={effectiveChoice}
              onChoice={selectPlan}
              platform={isNativePlatform ? 'native' : 'web'}
              ctaLabel={ctaLabel}
              onPurchase={handlePurchase}
              purchaseDisabled={!selected}
              onRestore={isNativePlatform ? handleRestore : undefined}
              errorMessage={errorMessage}
              onSignIn={onSignIn}
              phoneRestoreGuidance={!onSignIn}
            />
          )}

          {!funnelChrome && flowState === 'select' && !alreadyCovered && !playResubscribe && !eligibilityResolving && selected && (
            <div className="space-y-5">
              <div className="text-center">
                {/* Same trimmed layout for everyone (Justin's 2026-07-20
                    redesign); the LEAD forks on trial eligibility — the
                    no-trial copy is Justin's returning-customer markup, and a
                    blocked user never sees trial language anywhere. */}
                <h3 className="text-2xl font-serif text-growth">
                  {emphasizeBrand(trialAvailable ? 'Try all of enough. free.' : 'Choose your plan')}
                </h3>
                <p className="text-sm text-growth/70 mt-1">
                  {/* Trial length comes from the descriptor, never hardcoded:
                      native reads the store ("1 month"), web asserts "30 days"
                      — see the copy policy on TrialDescriptor. */}
                  {trialAvailable && trialForCopy
                    ? `Connect every account and align your spending with what matters — free for ${trialForCopy.periodLabel}.`
                    : trialAvailable
                      ? 'Connect every account and align your spending with what matters.'
                      : emphasizeBrand('Become a member to keep enjoying your enough. journey. Cancelling is free and easy.')}
                </p>
              </div>

              {/* Plan chooser — monthly-led, annual shown as the saving. Only a
                  chooser when the Offering actually returns both terms. */}
              {hasBoth ? (
                <div className="grid grid-cols-2 gap-3" role="radiogroup" aria-label="Billing period">
                  <PlanCard
                    label="Monthly"
                    priceString={stripWholeDollarCents(monthly!.priceString)}
                    suffix="/mo"
                    selected={effectiveChoice === 'monthly'}
                    onSelect={() => selectPlan('monthly')}
                  />
                  <PlanCard
                    label="Annual"
                    priceString={stripWholeDollarCents(annual!.priceString)}
                    suffix="/yr"
                    badge={savingsPct ? `Save ${savingsPct}%` : undefined}
                    subLabel={annualPerMonth ? `${formatUsd(annualPerMonth)}/mo` : undefined}
                    selected={effectiveChoice === 'annual'}
                    onSelect={() => selectPlan('annual')}
                  />
                </div>
              ) : (
                <div className="w-full p-4 rounded-2xl border-2 border-growth bg-growth/5 shadow-md">
                  <div className="flex items-center justify-between">
                    <div className="font-medium text-growth">
                      {effectiveChoice === 'annual' ? 'Annual' : 'Monthly'}
                    </div>
                    <div className="text-2xl font-serif text-growth">{stripWholeDollarCents(selected.priceString)}</div>
                  </div>
                </div>
              )}

              <SpendingToolsBenefits />

              {/* Honest trial terms — what you're charged and WHEN. Only when a
                  trial actually applies; otherwise the plan cards say it all. */}
              {trialForCopy && (
                <p className="text-xs text-growth/70 text-center px-2">{termsLine}</p>
              )}

              {errorMessage && (
                <div className="p-3 bg-wildfire/10 border border-wildfire/20 rounded-xl text-sm text-wildfire">
                  {errorMessage}
                </div>
              )}

              <button
                onClick={handlePurchase}
                disabled={!selected}
                className="w-full py-4 rounded-xl bg-growth text-cloud font-medium hover:bg-growth/90 transition-colors disabled:bg-mist disabled:text-growth/50 disabled:cursor-not-allowed"
              >
                {ctaLabel}
              </button>

              {/* Restore, led when the store hints they've been here before.
                  On native `!trialAvailable` means the STORE told us this Apple
                  ID / Play account has already used its intro offer for this
                  subscription group — which it can only have done by
                  subscribing before. The current account holds no entitlement,
                  so the likeliest truth is a returning member on a new uid
                  (reinstall, second email, shared device), and Restore is what
                  they need — not a second subscription. A hint only: Restore is
                  receipt-verified by the store, so being wrong costs nothing
                  but a tapped button. We never restore on their behalf. */}
              {isNativePlatform && (
                <button
                  onClick={handleRestore}
                  className={`w-full text-center text-sm transition-colors ${
                    trialAvailable
                      ? 'text-growth/50 hover:text-growth'
                      : 'font-medium text-growth hover:text-growth/70'
                  }`}
                >
                  {trialAvailable ? 'Restore purchases' : 'Already a member? Restore purchases'}
                </button>
              )}

              {/* Web has no restore (store receipts live on the phone). On a
                  post-login mount, a member whose subscription is an unrestored
                  store purchase gets pointed to the app instead of a second,
                  Stripe-billed subscription. Pre-login (onSignIn) mounts already
                  route existing members to sign-in below. Renders nothing on
                  native. */}
              {!onSignIn && <WebRestoreGuidance />}

              {onSignIn && (
                <button
                  onClick={onSignIn}
                  className="w-full text-center text-sm text-growth/50 hover:text-growth transition-colors"
                >
                  Already a member? <span className="underline">Sign in</span>
                </button>
              )}
            </div>
          )}

          {/* Web only (card T2): Stripe Elements payment step. */}
          {flowState === 'payment' && selected && (
            <div className="space-y-4">
              <p className="text-xs text-growth/70 text-center px-2">{termsLine}</p>
              <StripeCheckoutStep
                priceId={selected.productId}
                amount={selected.price}
                requestTrial={trialAvailable}
                ctaLabel={ctaLabel}
                onSuccess={handleWebPaymentSuccess}
                onBack={() => setFlowState('select')}
              />
            </div>
          )}

          {(flowState === 'purchasing' || flowState === 'restoring') && (
            <div className="flex flex-col items-center justify-center py-12">
              <div className={`w-10 h-10 border-4 rounded-full animate-spin ${tone.spinner}`} />
              <p className={`mt-4 ${tone.muted}`}>
                {flowState === 'purchasing' ? 'Completing your purchase...' : 'Restoring purchases...'}
              </p>
            </div>
          )}

          {flowState === 'success' && (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <div className={`w-16 h-16 rounded-full flex items-center justify-center mb-4 ${tone.disc}`}>
                <Icons.Check className={`w-8 h-8 ${tone.discIcon}`} />
              </div>
              <h3 className={`text-lg font-serif mb-2 ${tone.heading}`}>You're all set!</h3>
              <p className={`text-sm mb-6 ${tone.body}`}>
                {restoredExisting
                  ? 'We found your existing membership and brought it to this account — you haven’t been charged again.'
                  : 'Connect your accounts to see your whole financial picture.'}
              </p>
              {restoredNotRenewing && (
                <p className={`text-xs mb-6 px-2 ${tone.body}`}>
                  Heads up — this membership is set to end rather than renew. You have full access
                  until it expires; to keep it going, renew it from your device&apos;s subscription
                  settings.
                </p>
              )}
              {trialDeclined && (
                <p className={`text-xs mb-6 px-2 ${tone.body}`}>
                  Your free trial wasn't available as it was already used on this account. Your
                  membership will begin billing today. You can review or cancel anytime from Profile.
                </p>
              )}
              <button
                onClick={handleClose}
                className={`px-6 py-2.5 rounded-xl transition-colors font-medium ${tone.primaryBtn}`}
              >
                Done
              </button>
              {/* Push soft-ask backstop to the HubSpot trial-ending email — only
                  offered when a trial actually applied: on iOS `trialAvailable`
                  is Apple's own pre-purchase eligibility answer (fail-closed),
                  and `trialDeclined` covers the case where the store or our server
                  declined the requested trial. A restored membership started no
                  trial, whatever eligibility said pre-purchase. */}
              {trialAvailable && !trialDeclined && !restoredExisting && <TrialNotifyPrompt />}
            </div>
          )}

          {flowState === 'error' && (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <div className={`w-16 h-16 rounded-full flex items-center justify-center mb-4 ${tone.errorDisc}`}>
                <Icons.AlertCircle className="w-8 h-8 text-wildfire" />
              </div>
              <h3 className={`text-lg font-serif mb-2 ${tone.heading}`}>Something went wrong</h3>
              <p className={`text-sm mb-6 ${tone.body}`}>{errorMessage}</p>
              <button
                onClick={handleClose}
                className={`px-6 py-2.5 rounded-xl transition-colors font-medium ${tone.primaryBtn}`}
              >
                Close
              </button>
            </div>
          )}
        </div>

        {/* The funnel surface states these terms inside TrialPricingBox, right
            under the plan cards — repeating them in a footer rule would be
            noise, and the funnel screens have no footer chrome. */}
        {!funnelChrome && (
          <div className={`p-4 border-t ${tone.rule}`}>
            <p className={`text-xs text-center ${tone.fine}`}>
              Membership renews until you cancel. Cancel anytime from your profile.
            </p>
          </div>
        )}
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
};

/** A single selectable plan card in the monthly/annual chooser. */
const PlanCard: React.FC<{
  label: string;
  priceString: string;
  suffix: string;
  badge?: string;
  /** e.g. the annual plan's "$8.33/mo" monthly equivalent. */
  subLabel?: string;
  selected: boolean;
  onSelect: () => void;
}> = ({ label, priceString, suffix, badge, subLabel, selected, onSelect }) => (
  <button
    type="button"
    role="radio"
    aria-checked={selected}
    onClick={onSelect}
    className={`relative text-left p-4 rounded-2xl border-2 transition-colors ${
      selected ? 'border-growth bg-growth/5 shadow-md' : 'border-mist bg-cloud hover:border-growth/40'
    }`}
  >
    {badge && (
      <span className="absolute -top-2 right-3 px-2 py-0.5 rounded-full bg-foliage text-growth text-[10px] font-medium">
        {badge}
      </span>
    )}
    <div className="font-medium text-growth">{label}</div>
    <div className="mt-1 text-xl font-serif text-growth">
      {priceString}
      <span className="text-xs text-growth/60">{suffix}</span>
    </div>
    {subLabel && <div className="text-xs text-growth/60">{subLabel}</div>}
  </button>
);
